import { IconType } from "react-icons/lib";
import {
  MdAttachMoney,
  MdAssignment,
  MdDescription,
  MdEditDocument,
  MdGroups,
  MdListAlt,
  MdPerson,
  MdReceiptLong,
  MdShoppingCart,
} from "react-icons/md";

import { DocType } from "@megaron/docs-contracts";
import { getMegaraxPath } from "@megaron/megarax-uri";
import { isUuid, Uuid } from "@megaron/uuid";

const uuidPattern = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";

const crmColor = "hsl(273, 75%, 25%)";
const catalogColor = "hsl(150, 65%, 28%)";
const invoicesColor = "hsl(234, 75%, 25%)";
const iamColor = "hsl(191, 75%, 25%)";
const supportColor = "hsl(200, 65%, 35%)";

const uuidPath = (buildPath: (id: Uuid) => string) => (identifier: string) =>
  isUuid(identifier) ? buildPath(identifier) : undefined;

const buildPriceListPath = (id: string) =>
  getMegaraxPath.uri("megarax://catalog/price-lists", { filters: { priceLists: [id] } });

const matchGroup = (pattern: string) => {
  const regex = new RegExp(pattern);
  return (pathname: string): string | undefined => {
    const result = regex.exec(pathname);
    return result?.[1];
  };
};

export type HistoryDocRoute = {
  docType: DocType;
  label: string;
  appLabel: string;
  Icon: IconType;
  accentColor: string;
  matchIdentifier: (pathname: string) => string | undefined;
  buildPath: (identifier: string) => string | undefined;
};

export const historyDocRoutes: HistoryDocRoute[] = [
  {
    docType: "customer",
    label: "Klient",
    appLabel: "CRM",
    Icon: MdPerson,
    accentColor: crmColor,
    matchIdentifier: matchGroup(`^/crm/customers/id/(${uuidPattern})`),
    buildPath: uuidPath((id) => getMegaraxPath.uri("megarax://crm/customers/id/{id}", { id })),
  },
  {
    docType: "saved-view",
    label: "Lista CRM",
    appLabel: "CRM",
    Icon: MdListAlt,
    accentColor: crmColor,
    matchIdentifier: matchGroup(`^/crm/customers/(${uuidPattern})(?:/|$)`),
    buildPath: uuidPath((id) => getMegaraxPath.uri("megarax://crm/customer-lists/id/{id}", { id })),
  },
  {
    docType: "order",
    label: "Zamówienie",
    appLabel: "CRM",
    Icon: MdShoppingCart,
    accentColor: crmColor,
    matchIdentifier: matchGroup(`^/crm/orders/number/([^/]+)(?:/|$)`),
    buildPath: (number) => getMegaraxPath.uri("megarax://crm/orders/number/{number}", { number }),
  },
  {
    docType: "item",
    label: "Produkt",
    appLabel: "Katalog",
    Icon: MdDescription,
    accentColor: catalogColor,
    matchIdentifier: () => undefined,
    buildPath: () => getMegaraxPath.uri("megarax://catalog/items"),
  },
  {
    docType: "price-list",
    label: "Cennik",
    appLabel: "Katalog",
    Icon: MdAttachMoney,
    accentColor: catalogColor,
    matchIdentifier: () => undefined,
    buildPath: buildPriceListPath,
  },
  {
    docType: "invoice-thread",
    label: "Faktura",
    appLabel: "Faktury",
    Icon: MdReceiptLong,
    accentColor: invoicesColor,
    matchIdentifier: matchGroup(`^/invoices/threads/id/(${uuidPattern})`),
    buildPath: uuidPath((id) => getMegaraxPath.uri("megarax://invoices/threads/id/{id}", { id })),
  },
  {
    docType: "invoice-draft",
    label: "Wersja robocza",
    appLabel: "Faktury",
    Icon: MdEditDocument,
    accentColor: invoicesColor,
    matchIdentifier: matchGroup(`^/invoices/drafts/id/(${uuidPattern})`),
    buildPath: uuidPath((id) => getMegaraxPath.uri("megarax://invoices/drafts/id/{id}", { id })),
  },
  {
    docType: "budget",
    label: "Budżet",
    appLabel: "Faktury",
    Icon: MdAttachMoney,
    accentColor: invoicesColor,
    matchIdentifier: matchGroup(`^/invoices/budgets/id/(${uuidPattern})`),
    buildPath: uuidPath((id) => getMegaraxPath.uri("megarax://invoices/budgets/id/{id}", { id })),
  },
  {
    docType: "user",
    label: "Użytkownik",
    appLabel: "IAM",
    Icon: MdPerson,
    accentColor: iamColor,
    matchIdentifier: matchGroup(`^/iam/users/email/([^/]+)(?:/|$)`),
    buildPath: (id) => getMegaraxPath.uri("megarax://iam/users/email/{email}", { email: id }),
  },
  {
    docType: "user-group",
    label: "Grupa",
    appLabel: "IAM",
    Icon: MdGroups,
    accentColor: iamColor,
    matchIdentifier: matchGroup(`^/iam/groups/id/(${uuidPattern})`),
    buildPath: uuidPath((id) => getMegaraxPath.uri("megarax://iam/groups/id/{id}", { id })),
  },
  {
    docType: "support-ticket",
    label: "Zgłoszenie",
    appLabel: "Support",
    Icon: MdAssignment,
    accentColor: supportColor,
    matchIdentifier: matchGroup(`^/support/tickets/id/(${uuidPattern})`),
    buildPath: uuidPath((id) => getMegaraxPath.uri("megarax://support/tickets/id/{id}", { id })),
  },
];

export const fallbackDocIcon: IconType = MdDescription;

export const fallbackAccentColor = crmColor;

export const historyDocRouteByType = new Map(historyDocRoutes.map((route) => [route.docType, route]));
